/**
 * Cookie consent management for LGPD/GDPR compliance.
 *
 * Categories:
 * - essential: always on (auth session, CSRF, consent preference itself)
 * - analytics: PostHog pageviews, feature flags, session recording opt-in
 * - monitoring: Datadog RUM error tracking, session replay
 *
 * LGPD requires: opt-in (no pre-checked non-essential), easy withdrawal,
 * clear information about each category.
 */

export type CookieCategory = "essential" | "analytics" | "monitoring";

export interface CookieConsent {
  essential: true; // always true, cannot be disabled
  analytics: boolean;
  monitoring: boolean;
  timestamp: string; // ISO 8601
  version: string;
}

const CONSENT_KEY = "alana_cookie_consent";
const CONSENT_VERSION = "1.0";

export function getCookieConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    // Validate shape
    if (
      parsed.version &&
      typeof parsed.analytics === "boolean" &&
      typeof parsed.monitoring === "boolean"
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function setCookieConsent(
  preferences: Pick<CookieConsent, "analytics" | "monitoring">,
): CookieConsent {
  const consent: CookieConsent = {
    essential: true,
    analytics: preferences.analytics,
    monitoring: preferences.monitoring,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));

  // Sync to HTTP cookie so server-side code (proxy, API routes) can read consent
  document.cookie = `${CONSENT_KEY}=${encodeURIComponent(JSON.stringify(consent))};path=/;max-age=${365 * 24 * 60 * 60};samesite=lax;secure`;

  // Dispatch event so providers can react immediately
  window.dispatchEvent(
    new CustomEvent("cookie-consent-change", { detail: consent }),
  );

  return consent;
}

export function hasConsentBeenGiven(): boolean {
  return getCookieConsent() !== null;
}

export function isAnalyticsAllowed(): boolean {
  return getCookieConsent()?.analytics === true;
}

export function isMonitoringAllowed(): boolean {
  return getCookieConsent()?.monitoring === true;
}

export function clearCookieConsent(): void {
  localStorage.removeItem(CONSENT_KEY);
  // Clear HTTP cookie
  document.cookie = `${CONSENT_KEY}=;path=/;max-age=0;samesite=lax;secure`;
}
